import { RefObject } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Search, Filter, X } from 'lucide-react';
import SearchableSelect from '../../components/ui/SearchableSelect';
import {
  buildOtdQuickFilterChips,
  type OtdBand,
  type OtdThresholds,
} from './otdFilters';

/** CR-3953: Filter options for the customer list */
export interface CustomerFilters {
  site: string;
  status: string;
  customer_code: string;
  strategic: string;
  risk: string;
  questionnaire: string;
  otd: OtdBand | '';
}

export const EMPTY_FILTERS: CustomerFilters = {
  site: '',
  status: '',
  customer_code: '',
  strategic: '',
  risk: '',
  questionnaire: '',
  otd: '',
};

interface SearchFilterBarProps {
  searchRef: RefObject<HTMLInputElement | null>;
  showFilters: boolean;
  filters?: CustomerFilters;
  riskCounts?: Record<string, number>;
  questionnaireCounts?: Record<string, number>;
  otdCounts?: Record<string, number>;
  otdThresholds?: OtdThresholds;
  showOtdQuickFilters?: boolean;
  siteOptions?: { value: string; label: string }[];
  customerCodeOptions?: { value: string; label: string }[];
  searchQuery?: string;
  onSearch: () => void;
  onSearchKeyDown: (e: React.KeyboardEvent) => void;
  onClearSearch?: () => void;
  onToggleFilters: () => void;
  onFiltersChange?: (filters: CustomerFilters) => void;
}

export function SearchFilterBar({
  searchRef,
  showFilters,
  filters = EMPTY_FILTERS,
  otdCounts = {},
  otdThresholds,
  showOtdQuickFilters = true,
  siteOptions = [],
  customerCodeOptions = [],
  searchQuery = '',
  onSearch,
  onSearchKeyDown,
  onClearSearch,
  onToggleFilters,
  onFiltersChange,
}: SearchFilterBarProps) {
  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const otdChips = otdThresholds ? buildOtdQuickFilterChips(otdThresholds) : [];

  const handleFilterChange = (key: keyof CustomerFilters, value: string) => {
    onFiltersChange?.({ ...filters, [key]: value });
  };

  const handleClearFilters = () => {
    onFiltersChange?.({ ...EMPTY_FILTERS });
  };

  const handleOtdChipClick = (band: OtdBand | '') => {
    onFiltersChange?.({ ...filters, otd: filters.otd === band ? '' : band });
  };

  return (
    <div className="space-y-4">
      {/* Search bar + filter toggle */}
      <div className="flex gap-4 items-center">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={searchRef}
            placeholder="Search customers..."
            onKeyDown={onSearchKeyDown}
            className="pl-10 pr-10 border border-input bg-background"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                if (searchRef.current) searchRef.current.value = '';
                onClearSearch?.();
              }}
              aria-label="Clear search"
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Button onClick={onSearch} variant="outline">
          <Search className="h-4 w-4" />
        </Button>
        <Button
          variant={showFilters ? 'default' : 'outline'}
          onClick={onToggleFilters}
        >
          <Filter className="h-4 w-4 mr-2" />
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-2 bg-white text-primary rounded-full w-5 h-5 flex items-center justify-center text-xs">
              {activeFilterCount}
            </span>
          )}
        </Button>
      </div>

      {/* OTD quick-filter chips */}
      {showOtdQuickFilters && otdChips.length > 0 && (
        <div className="space-y-2" data-tour="otd-quick-filters">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            OTD quick filters
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => handleOtdChipClick('')}
              className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                filters.otd === ''
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-foreground border-border hover:bg-muted'
              }`}
            >
              All
              {typeof otdCounts[''] === 'number' ? ` (${otdCounts['']})` : ''}
            </button>
            {otdChips.map((chip) => (
              <button
                key={chip.value}
                type="button"
                onClick={() => handleOtdChipClick(chip.value)}
                className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  filters.otd === chip.value ? chip.activeClassName : chip.idleClassName
                }`}
              >
                {chip.label}
                {typeof otdCounts[chip.value] === 'number' ? ` (${otdCounts[chip.value]})` : ''}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Inline collapsible filter panel */}
      {showFilters && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Filter Customers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {siteOptions.length > 0 && (
                <div>
                  <label className="text-sm font-medium mb-2 block">Company Site</label>
                  <SearchableSelect
                    options={siteOptions}
                    value={filters.site}
                    onChange={(val) => handleFilterChange('site', val)}
                    placeholder="All Company Sites"
                  />
                </div>
              )}

              {customerCodeOptions.length > 0 && (
                <div>
                  <label className="text-sm font-medium mb-2 block">Customer Code</label>
                  <SearchableSelect
                    options={customerCodeOptions}
                    value={filters.customer_code}
                    onChange={(val) => handleFilterChange('customer_code', val)}
                    placeholder="Search by code or name…"
                  />
                </div>
              )}

              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.strategic === 'yes'}
                    onChange={(e) => handleFilterChange('strategic', e.target.checked ? 'yes' : '')}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <span className="text-sm font-medium">Strategic Only</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
              {activeFilterCount > 0 && (
                <Button variant="outline" onClick={handleClearFilters} className="px-6">
                  <X className="h-4 w-4 mr-2" />
                  Clear All
                </Button>
              )}
              <Button onClick={onSearch} className="px-6">
                <Search className="h-4 w-4 mr-2" />
                Search
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
